import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Save, Trash2, X } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';
import '../styles/Builders.css';
import '../styles/Buttons.css';

interface Workspace {
  _id: string;
  name: string;
  description?: string;
  owner: string;
  showDelete?: boolean;
}

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { workspaceId } = useParams<{ workspaceId: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchWorkspace();
  }, [workspaceId]);

  const fetchWorkspace = async () => {
    if (!workspaceId) return;

    try {
      setLoading(true);
      const response = await api.get(`/workspaces/${workspaceId}`);
      setWorkspace(response.data.workspace);
      setFormData({
        name: response.data.workspace.name,
        description: response.data.workspace.description || ''
      });
    } catch (err: any) {
      console.error('Error fetching workspace:', err);
      toast.error('Failed to load workspace settings');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToWorkspaces = () => {
    navigate('/workspaces');
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Workspace name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm() || !workspaceId) return;

    try {
      setSaving(true);
      await api.put(`/workspaces/${workspaceId}`, formData);
      toast.success('Workspace settings updated successfully');
      fetchWorkspace();
    } catch (err: any) {
      console.error('Error updating workspace:', err);
      toast.error(err.response?.data?.message || 'Failed to update workspace settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!workspaceId) return;

    try {
      setDeleting(true);
      await api.delete(`/workspaces/${workspaceId}`);
      toast.success('Workspace deleted successfully');
      navigate('/workspaces');
    } catch (err: any) {
      console.error('Error deleting workspace:', err);
      toast.error(err.response?.data?.message || 'Failed to delete workspace');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <AppLayout onBack={handleBackToWorkspaces} backLabel="Workspaces">
      <div className="builders-container">
        {/* Page Header */}
        <div className="builders-section-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}>
          <div>
            <h1 className="builders-section-title">Workspace Settings</h1>
            <p className="builders-section-subtitle">
              Manage your workspace configuration
            </p>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="builders-loading">
            <div className="builders-loading-spinner" />
          </div>
        ) : (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            width: '100%'
          }}>
            <div style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '12px',
              padding: '32px',
              boxShadow: '0 4px 12px rgba(72, 24, 47, 0.15), 0 2px 4px rgba(72, 24, 47, 0.08)',
              maxWidth: '800px',
              width: '100%'
            }}>
            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#48182F',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Workspace Name *
              </label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter workspace name"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: errors.name ? '1px solid #DC2626' : '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#48182F',
                  boxSizing: 'border-box'
                }}
              />
              {errors.name && (
                <p style={{
                  color: '#DC2626',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>{errors.name}</p>
              )}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label className="form-label" style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#48182F',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Description
              </label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter workspace description"
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#48182F',
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              marginTop: '32px'
            }}>
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary btn-md"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            </div>

            {/* Delete Workspace Section */}
            {workspace && workspace.showDelete && (
              <div style={{
                backgroundColor: '#FEF2F2',
                borderRadius: '12px',
                padding: '32px',
                border: '1px solid #FEE2E2',
                maxWidth: '800px',
                width: '100%'
              }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#DC2626',
              marginBottom: '8px'
            }}>
              Delete Workspace
            </h2>
            <p style={{
              color: '#6B7280',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              Permanently delete this workspace and all of its data including files from S3. This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="btn btn-md"
                style={{
                  backgroundColor: '#DC2626',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Trash2 size={18} />
                Delete Workspace
              </button>
            </div>
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }} onClick={() => setShowDeleteModal(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{
                padding: '20px 24px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#DC2626',
                  margin: 0
                }}>
                  Delete Workspace
                </h2>
                <button
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    color: '#6B7280'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ padding: '24px' }}>
                <p style={{
                  color: '#374151',
                  fontSize: '14px',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  Are you sure you want to delete <strong>{workspace?.name}</strong>?
                </p>
                <p style={{
                  color: '#DC2626',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginBottom: '8px'
                }}>
                  This will permanently delete:
                </p>
                <ul style={{
                  color: '#6B7280',
                  fontSize: '14px',
                  marginLeft: '20px',
                  lineHeight: '1.8'
                }}>
                  <li>All workspace data</li>
                  <li>All users and roles</li>
                  <li>All files and images from S3</li>
                  <li>All associated records</li>
                </ul>
                <p style={{
                  color: '#DC2626',
                  fontSize: '14px',
                  marginTop: '16px',
                  fontWeight: '600'
                }}>
                  This action cannot be undone.
                </p>
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                padding: '20px 24px',
                borderTop: '1px solid #E5E7EB'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: '1px solid #D1D5DB'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteWorkspace}
                  disabled={deleting}
                  className="btn"
                  style={{
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    backgroundColor: '#DC2626',
                    color: 'white',
                    border: 'none',
                    cursor: deleting ? 'not-allowed' : 'pointer',
                    opacity: deleting ? 0.6 : 1
                  }}
                >
                  {deleting ? 'Deleting...' : 'Delete Workspace'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default Settings;
